import React, { useState, useEffect } from 'react';
import { GoogleLogin, googleLogout, useGoogleOneTapLogin } from '@react-oauth/google';
import './Profile.css';
import { getGoogleToken, storeGoogleToken, clearGoogleToken } from '../utils/googleAuth';
import useICalEvents from '../hooks/useICalEventsNew';
import { validateICalUrl } from '../utils/icalUtils';
import { useOutlookEvents } from '../hooks/useOutlookEvents';
import { isZoomConnected, initiateZoomAuth, disconnectZoom, getZoomUserInfo } from '../utils/zoomUtils';
import { isTeamsConnected, getTeamsConnectionInfo, testTeamsConnection } from '../utils/teamsUtils';
import { isGmailAvailable } from '../utils/gmailUtils';

function Profile() {
  // Simplified Google OAuth availability check
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  const isGoogleOAuthAvailable = !!(clientId && clientId.trim() && clientId !== 'your_actual_google_client_id_here');
  
  // Google token state - initialize from localStorage using utility
  const [googleToken, setGoogleTokenState] = useState(() => {
    return getGoogleToken();
  });

  // Profile state
  const [profile, setProfile] = useState(() => {
    const stored = localStorage.getItem("userProfile");
    return stored ? JSON.parse(stored) : {
      name: "",
      email: "",
      bio: "",
      phone: "",
      website: "",
      location: "",
      timezone: "",
      avatar: "",
      socials: {
        twitter: "",
        linkedin: "",
        tiktok: "",
        instagram: "",
        facebook: ""
      },
      integrations: {
        google: null,
        notion: null,
        trello: null,
        ical: {
          url: "",
          connected: false,
          lastSync: null
        },
        outlook: {
          connected: false,
          userInfo: null,
          lastSync: null
        },
        zoom: {
          connected: false,
          userInfo: null,
          lastSync: null
        }
      },
      preferences: {
        theme: "light",
        notifications: true,
        emailReminders: true,
        defaultTaskDuration: 60,
        defaultMeetingType: "", // NEW: Default meeting type preference
        workingHours: {
          start: "09:00",
          end: "17:00"
        }
      }
    };
  });

  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState("personal");
  
  // iCal integration states
  const [icalUrl, setIcalUrl] = useState(profile.integrations.ical?.url || "");
  const [icalTesting, setIcalTesting] = useState(false);
  const [icalTestResult, setIcalTestResult] = useState(null);
  
  // Use iCal hook
  const { 
    events: icalEvents, 
    loading: icalLoading, 
    error: icalError, 
    testUrl, 
    refreshEvents: refreshIcalEvents 
  } = useICalEvents(profile.integrations.ical?.connected ? profile.integrations.ical.url : null);

  // Use Outlook hook
  const {
    events: outlookEvents,
    isLoading: outlookLoading,
    isConnected: outlookConnected,
    error: outlookError,
    userInfo: outlookUserInfo,
    lastSync: outlookLastSync,
    connect: connectOutlook,
    disconnect: disconnectOutlook,
    refresh: refreshOutlookEvents,
    testConnection: testOutlookConnection
  } = useOutlookEvents();

  // Zoom integration states
  const [zoomConnected, setZoomConnected] = useState(() => isZoomConnected());
  const [zoomUserInfo, setZoomUserInfo] = useState(null);

  // Teams integration states
  const [teamsConnected, setTeamsConnected] = useState(() => isTeamsConnected());
  const [teamsConnectionInfo, setTeamsConnectionInfo] = useState(() => getTeamsConnectionInfo());

  // Check Zoom connection on component mount
  useEffect(() => {
    const checkZoomConnection = async () => {
      const connected = isZoomConnected();
      setZoomConnected(connected);
      
      if (connected) {
        try {
          const userInfo = await getZoomUserInfo();
          setZoomUserInfo(userInfo);
        } catch (error) {
          console.error('Failed to get Zoom user info:', error);
          setZoomConnected(false);
        }
      }
    };
    
    checkZoomConnection();
  }, []);

  // Save profile to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("userProfile", JSON.stringify(profile));
    window.dispatchEvent(new Event('aidey:onboardingProgress'));
  }, [profile]);

  // Sync Outlook connection state with profile
  useEffect(() => {
    if (outlookConnected !== profile.integrations.outlook?.connected) {
      setProfile(prev => ({
        ...prev,
        integrations: {
          ...prev.integrations,
          outlook: {
            connected: outlookConnected,
            userInfo: outlookUserInfo,
            lastSync: outlookLastSync
          }
        }
      }));
    }
  }, [outlookConnected, outlookUserInfo, outlookLastSync, profile.integrations.outlook?.connected]);

  // Sync Zoom connection state with profile
  useEffect(() => {
    if (zoomConnected !== profile.integrations.zoom?.connected) {
      setProfile(prev => ({
        ...prev,
        integrations: {
          ...prev.integrations,
          zoom: {
            connected: zoomConnected,
            userInfo: zoomUserInfo,
            lastSync: zoomConnected ? new Date().toISOString() : null
          }
        }
      }));
    }
  }, [zoomConnected, zoomUserInfo, profile.integrations.zoom?.connected]);

  // Sync Teams connection with Outlook connection
  useEffect(() => {
    const updateTeamsConnection = () => {
      const connected = isTeamsConnected();
      const connectionInfo = getTeamsConnectionInfo();
      
      setTeamsConnected(connected);
      setTeamsConnectionInfo(connectionInfo);
    };

    updateTeamsConnection();
    
    // Update Teams connection when Outlook connection changes
    if (outlookConnected) {
      updateTeamsConnection();
    }
  }, [outlookConnected]);

  // Check if Google token is available on component mount
  useEffect(() => {
    if (googleToken && !profile.integrations.google) {
      // Token exists but profile doesn't show connected state
      // This can happen on page refresh - try to decode the existing token
      try {
        const userInfo = JSON.parse(atob(googleToken.split('.')[1]));
        setProfile(prev => ({
          ...prev,
          integrations: {
            ...prev.integrations,
            google: {
              connected: true,
              email: userInfo.email,
              name: userInfo.name,
              token: googleToken,
              connectedAt: new Date().toISOString()
            }
          }
        }));
      } catch (error) {
        // Token is invalid, remove it
        clearGoogleToken();
        setGoogleTokenState(null);
      }
    }
  }, [googleToken, profile.integrations.google]);

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setProfile(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setProfile(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleGoogleSuccess = (credentialResponse) => {
    // Store the token for later use using utility function
    const token = credentialResponse.credential;
    storeGoogleToken(token);
    setGoogleTokenState(token);
    
    // Decode the JWT token to get user info
    const userInfo = JSON.parse(atob(token.split('.')[1]));
    
    setProfile(prev => ({
      ...prev,
      name: prev.name || userInfo.name,
      email: prev.email || userInfo.email,
      avatar: prev.avatar || userInfo.picture,
      integrations: {
        ...prev.integrations,
        google: {
          connected: true,
          email: userInfo.email,
          name: userInfo.name,
          token: token, // Store token in profile state as well
          connectedAt: new Date().toISOString()
        }
      }
    }));
  };

  const handleGoogleLogout = () => {
    googleLogout();
    // Clear stored token using utility function
    clearGoogleToken();
    setGoogleTokenState(null);
    
    setProfile(prev => ({
      ...prev,
      integrations: {
        ...prev.integrations,
        google: null
      }
    }));
  };

  // iCal Integration Handlers
  const handleIcalTest = async () => {
    if (!icalUrl.trim()) {
      setIcalTestResult({ success: false, error: "Please enter an iCal URL" });
      return;
    }

    const validation = validateICalUrl(icalUrl);
    if (!validation.valid) {
      setIcalTestResult({ success: false, error: validation.error });
      return;
    }

    setIcalTesting(true);
    setIcalTestResult(null);

    try {
      const result = await testUrl(icalUrl);
      setIcalTestResult(result);
    } catch (error) {
      setIcalTestResult({ success: false, error: error.message });
    } finally {
      setIcalTesting(false);
    }
  };

  const handleIcalConnect = () => {
    if (!icalTestResult?.success) {
      alert("Please test the URL first to make sure it works");
      return;
    }

    setProfile(prev => ({
      ...prev,
      integrations: {
        ...prev.integrations,
        ical: {
          url: icalUrl,
          connected: true,
          lastSync: new Date().toISOString()
        }
      }
    }));

    setIcalTestResult(null);
  };

  const handleIcalDisconnect = () => {
    setProfile(prev => ({
      ...prev,
      integrations: {
        ...prev.integrations,
        ical: {
          url: "",
          connected: false,
          lastSync: null
        }
      }
    }));
    setIcalUrl("");
    setIcalTestResult(null);
  };

  // Outlook integration handlers
  const handleOutlookConnect = async () => {
    try {
      const result = await connectOutlook();
      if (result.success) {
        // Profile state will be updated automatically via useEffect
        console.log('Outlook connected successfully');
      } else {
        console.error('Outlook connection failed:', result.message);
      }
    } catch (error) {
      console.error('Error connecting to Outlook:', error);
    }
  };

  const handleOutlookDisconnect = async () => {
    try {
      await disconnectOutlook();
      setProfile(prev => ({
        ...prev,
        integrations: {
          ...prev.integrations,
          outlook: {
            connected: false,
            userInfo: null,
            lastSync: null
          }
        }
      }));
    } catch (error) {
      console.error('Error disconnecting from Outlook:', error);
    }
  };

  const handleOutlookTest = async () => {
    try {
      const result = await testOutlookConnection();
      if (result.success) {
        alert(`✅ ${result.message}`);
      } else {
        alert(`❌ ${result.message}`);
      }
    } catch (error) {
      alert(`❌ Test failed: ${error.message}`);
    }
  };

  // Zoom integration handlers
  const handleZoomConnect = async () => {
    try {
      initiateZoomAuth();
    } catch (error) {
      console.error('Failed to initiate Zoom auth:', error);
      alert('Failed to connect to Zoom. Please try again.');
    }
  };

  const handleZoomDisconnect = async () => {
    try {
      disconnectZoom();
      setZoomConnected(false);
      setZoomUserInfo(null);
      alert('Successfully disconnected from Zoom');
    } catch (error) {
      console.error('Failed to disconnect from Zoom:', error);
      alert('Failed to disconnect from Zoom. Please try again.');
    }
  };

  // Teams integration handlers
  const handleTeamsTest = async () => {
    try {
      const result = await testTeamsConnection();
      if (result.success) {
        alert(`✅ ${result.message}`);
      } else {
        alert(`❌ ${result.message}`);
      }
    } catch (error) {
      alert(`❌ Teams test failed: ${error.message}`);
    }
  };

  return (
    <div className="profile-container">
      <div className="profile-header">
        <div className="profile-avatar-section">
          {profile.avatar ? (
            <img src={profile.avatar} alt="Profile" className="profile-avatar" />
          ) : (
            <div className="profile-avatar-placeholder">
              {profile.name ? profile.name.charAt(0).toUpperCase() : "U"}
            </div>
          )}
          <div className="profile-info">
            <h2>{profile.name || "Your Name"}</h2>
            <p>{profile.email || "your.email@example.com"}</p>
            <p>{profile.location || "Add your location"}</p>
          </div>
        </div>
        <button 
          className="edit-profile-btn"
          onClick={() => setIsEditing(!isEditing)}
        >
          {isEditing ? "Save Changes" : "Edit Profile"}
        </button>
      </div>

      <div className="profile-tabs">
        <button 
          className={`tab ${activeTab === "personal" ? "active" : ""}`}
          onClick={() => setActiveTab("personal")}
        >
          Personal Info
        </button>
        <button 
          className={`tab ${activeTab === "socials" ? "active" : ""}`}
          onClick={() => setActiveTab("socials")}
        >
          Social Links
        </button>
        <button 
          className={`tab ${activeTab === "integrations" ? "active" : ""}`}
          onClick={() => setActiveTab("integrations")}
        >
          Integrations
        </button>
        <button 
          className={`tab ${activeTab === "preferences" ? "active" : ""}`}
          onClick={() => setActiveTab("preferences")}
        >
          Preferences
        </button>
      </div>

      <div className="profile-content">
        {activeTab === "personal" && (
          <div className="profile-section">
            <h3>Personal Information</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Full Name</label>
                <input
                  type="text"
                  value={profile.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  disabled={!isEditing}
                  placeholder="Enter your full name"
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={profile.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  disabled={!isEditing}
                  placeholder="Enter your email"
                />
              </div>
              <div className="form-group">
                <label>Phone Number</label>
                <input
                  type="tel"
                  value={profile.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  disabled={!isEditing}
                  placeholder="Enter your phone number"
                />
              </div>
              <div className="form-group">
                <label>Website</label>
                <input
                  type="url"
                  value={profile.website}
                  onChange={(e) => handleInputChange('website', e.target.value)}
                  disabled={!isEditing}
                  placeholder="https://yourwebsite.com"
                />
              </div>
              <div className="form-group">
                <label>Location</label>
                <input
                  type="text"
                  value={profile.location}
                  onChange={(e) => handleInputChange('location', e.target.value)}
                  disabled={!isEditing}
                  placeholder="City, Country"
                />
              </div>
              <div className="form-group">
                <label>Timezone</label>
                <select
                  value={profile.timezone}
                  onChange={(e) => handleInputChange('timezone', e.target.value)}
                  disabled={!isEditing}
                >
                  <option value="">Select Timezone</option>
                  <option value="UTC">UTC</option>
                  <option value="America/New_York">Eastern Time</option>
                  <option value="America/Chicago">Central Time</option>
                  <option value="America/Denver">Mountain Time</option>
                  <option value="America/Los_Angeles">Pacific Time</option>
                  <option value="Europe/London">London</option>
                  <option value="Europe/Paris">Paris</option>
                  <option value="Asia/Tokyo">Tokyo</option>
                  <option value="Asia/Shanghai">Shanghai</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>Bio</label>
              <textarea
                value={profile.bio}
                onChange={(e) => handleInputChange('bio', e.target.value)}
                disabled={!isEditing}
                placeholder="Tell us about yourself..."
                rows="4"
              />
            </div>
          </div>
        )}

        {activeTab === "socials" && (
          <div className="profile-section">
            <h3>Social Media Links</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>🐦 Twitter</label>
                <input
                  type="url"
                  value={profile.socials.twitter}
                  onChange={(e) => handleInputChange('socials.twitter', e.target.value)}
                  disabled={!isEditing}
                  placeholder="https://twitter.com/username"
                />
              </div>
              <div className="form-group">
                <label>💼 LinkedIn</label>
                <input
                  type="url"
                  value={profile.socials.linkedin}
                  onChange={(e) => handleInputChange('socials.linkedin', e.target.value)}
                  disabled={!isEditing}
                  placeholder="https://linkedin.com/in/username"
                />
              </div>
              <div className="form-group">
                <label>🎵 TikTok</label>
                <input
                  type="url"
                  value={profile.socials.tiktok}
                  onChange={(e) => handleInputChange('socials.tiktok', e.target.value)}
                  disabled={!isEditing}
                  placeholder="https://tiktok.com/@username"
                />
              </div>
              <div className="form-group">
                <label>📸 Instagram</label>
                <input
                  type="url"
                  value={profile.socials.instagram}
                  onChange={(e) => handleInputChange('socials.instagram', e.target.value)}
                  disabled={!isEditing}
                  placeholder="https://instagram.com/username"
                />
              </div>
              <div className="form-group">
                <label>👤 Facebook</label>
                <input
                  type="url"
                  value={profile.socials.facebook}
                  onChange={(e) => handleInputChange('socials.facebook', e.target.value)}
                  disabled={!isEditing}
                  placeholder="https://facebook.com/username"
                />
              </div>
            </div>
          </div>
        )}

        {activeTab === "integrations" && (
          <div className="profile-section">
            <h3>API Integrations</h3>
            
            {/* Calendars & Scheduling Category */}
            <div className="integration-category">
              <h4 className="category-header">📅 Calendars & Scheduling</h4>
              <div className="integrations-grid">
                
                {/* Google Integration */}
                <div className="integration-card">
                  <div className="integration-header">
                    <h4>🔍 Google Calendar</h4>
                    <span className={`status ${profile.integrations.google ? 'connected' : 'disconnected'}`}>
                      {profile.integrations.google ? 'Connected' : 'Not Connected'}
                    </span>
                  </div>
                  <p>Connect Google Calendar, Gmail, and Drive for seamless task integration</p>
                  {!isGoogleOAuthAvailable ? (
                    <div className="oauth-setup-notice">
                      <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '10px' }}>
                        ℹ️ Google integration requires OAuth setup
                      </p>
                      <p style={{ fontSize: '0.8rem', color: '#999', marginBottom: '10px' }}>
                        Add your Google Client ID to enable this feature
                      </p>
                      <details style={{ fontSize: '0.8rem', color: '#666' }}>
                        <summary style={{ cursor: 'pointer', fontWeight: '600' }}>How to set up Google OAuth</summary>
                        <ol style={{ marginTop: '8px', paddingLeft: '20px' }}>
                          <li>Go to <a href="https://console.cloud.google.com/" target="_blank" rel="noopener noreferrer">Google Cloud Console</a></li>
                          <li>Create a new project or select existing one</li>
                          <li>Enable Google Calendar API</li>
                          <li>Create OAuth 2.0 Client ID credentials</li>
                          <li>Add your domain to authorized origins</li>
                          <li>Copy Client ID to your .env file as REACT_APP_GOOGLE_CLIENT_ID</li>
                        </ol>
                      </details>
                    </div>
                  ) : !profile.integrations.google ? (
                    <div>
                      {clientId === 'demo-client-id-for-testing' && (
                        <div className="demo-mode-notice" style={{ marginBottom: '1rem' }}>
                          <p style={{ fontSize: '0.9rem', color: '#e67e22', marginBottom: '5px' }}>
                            🧪 Demo Mode - Login will not work with demo credentials
                          </p>
                        </div>
                      )}
                      <GoogleLogin
                        onSuccess={handleGoogleSuccess}
                        onError={() => console.log('Login Failed')}
                        text="continue_with"
                        shape="rectangular"
                      />
                    </div>
                  ) : (
                    <div>
                      <p className="connected-info">
                        Connected as: {profile.integrations.google.email}
                      </p>
                      <p className="connected-info" style={{ fontSize: '0.8rem', marginTop: '0.25rem' }}>
                        Token stored ✓ - Ready for Calendar API calls
                      </p>
                      <button onClick={handleGoogleLogout} className="disconnect-btn">
                        Disconnect
                      </button>
                    </div>
                  )}
                </div>

                {/* Outlook Integration */}
                <div className="integration-card">
                  <div className="integration-header">
                    <h4>� Microsoft Outlook</h4>
                    <span className={`status ${outlookConnected ? 'connected' : 'disconnected'}`}>
                      {outlookConnected ? 'Connected' : 'Not Connected'}
                    </span>
                  </div>
                  <p>Connect your Microsoft 365 / Outlook calendar with full sync</p>
                  
                  {!outlookConnected ? (
                    <div className="outlook-setup">
                      <button 
                        onClick={handleOutlookConnect}
                        className="connect-btn outlook-connect"
                        disabled={outlookLoading}
                      >
                        {outlookLoading ? 'Connecting...' : '🔷 Connect Outlook'}
                      </button>
                      
                      {outlookError && (
                        <div className="test-result error">
                          <p>❌ Error: {outlookError}</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="outlook-connected">
                      <p className="connected-info">
                        👤 Connected as: {outlookUserInfo?.displayName || outlookUserInfo?.userPrincipalName}
                      </p>
                      <p className="connected-info" style={{ fontSize: '0.8rem', marginTop: '0.25rem' }}>
                        {outlookEvents.length} events loaded • Last sync: {outlookLastSync ? new Date(outlookLastSync).toLocaleString() : 'Never'}
                      </p>
                      {outlookError && (
                        <p className="error-info" style={{ color: 'red', fontSize: '0.8rem' }}>
                          ⚠️ {outlookError}
                        </p>
                      )}
                      <div className="outlook-actions">
                        <button onClick={handleOutlookTest} className="test-btn" disabled={outlookLoading}>
                          {outlookLoading ? 'Testing...' : 'Test Connection'}
                        </button>
                        <button onClick={refreshOutlookEvents} className="refresh-btn" disabled={outlookLoading}>
                          {outlookLoading ? 'Syncing...' : 'Refresh'}
                        </button>
                        <button onClick={handleOutlookDisconnect} className="disconnect-btn">
                          Disconnect
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* iCal Integration */}
                <div className="integration-card">
                  <div className="integration-header">
                    <h4>📅 iCal Calendar</h4>
                    <span className={`status ${profile.integrations.ical?.connected ? 'connected' : 'disconnected'}`}>
                      {profile.integrations.ical?.connected ? 'Connected' : 'Not Connected'}
                    </span>
                  </div>
                  <p>Connect any calendar via iCal URL (Apple, Outlook, etc.)</p>
                  
                  {!profile.integrations.ical?.connected ? (
                    <div className="ical-setup">
                      <div className="input-group">
                        <input
                          type="url"
                          placeholder="Paste your iCal URL here (e.g., webcal://...)"
                          value={icalUrl}
                          onChange={(e) => setIcalUrl(e.target.value)}
                          className="ical-url-input"
                          disabled={icalTesting}
                        />
                        <button 
                          onClick={handleIcalTest}
                          className="test-btn"
                          disabled={icalTesting || !icalUrl.trim()}
                        >
                          {icalTesting ? 'Testing...' : 'Test'}
                        </button>
                      </div>
                      
                      {icalTestResult && (
                        <div className={`test-result ${icalTestResult.success ? 'success' : 'error'}`}>
                          {icalTestResult.success ? (
                            <div>
                              <p>✅ Success! Found {icalTestResult.eventCount} events</p>
                              {icalTestResult.events?.length > 0 && (
                                <div className="preview-events">
                                  <p><strong>Preview:</strong></p>
                                  <ul>
                                    {icalTestResult.events.slice(0, 3).map((event, idx) => (
                                      <li key={idx}>{event.title} - {event.date}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              <button onClick={handleIcalConnect} className="connect-btn">
                                Connect Calendar
                              </button>
                            </div>
                          ) : (
                            <p>❌ Error: {icalTestResult.error}</p>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="ical-connected">
                      <p className="connected-info">
                        📡 Connected to: {profile.integrations.ical.url}
                      </p>
                      <p className="connected-info" style={{ fontSize: '0.8rem', marginTop: '0.25rem' }}>
                        {icalEvents.length} events loaded • Last sync: {new Date(profile.integrations.ical.lastSync).toLocaleString()}
                      </p>
                      {icalError && (
                        <p className="error-info" style={{ color: 'red', fontSize: '0.8rem' }}>
                          ⚠️ {icalError}
                        </p>
                      )}
                      <div className="ical-actions">
                        <button onClick={refreshIcalEvents} className="refresh-btn" disabled={icalLoading}>
                          {icalLoading ? 'Syncing...' : 'Refresh'}
                        </button>
                        <button onClick={handleIcalDisconnect} className="disconnect-btn">
                          Disconnect
                        </button>
                      </div>
                    </div>
                  )}
                </div>

              </div>
            </div>

            {/* Productivity & Task Management Category */}
            <div className="integration-category">
              <h4 className="category-header">⚡ Productivity & Task Management</h4>
              <div className="integrations-grid">

                {/* Notion Integration */}
                <div className="integration-card integration-card-coming-soon">
                  <div className="integration-header">
                    <h4>📝 Notion</h4>
                    <span className="status coming-soon">Coming Soon</span>
                  </div>
                  <p>Export tasks and notes to Notion databases</p>
                  <button 
                    className="connect-btn"
                    disabled
                  >
                    Coming Soon
                  </button>
                </div>

                {/* Trello Integration */}
                <div className="integration-card integration-card-coming-soon">
                  <div className="integration-header">
                    <h4>📋 Trello</h4>
                    <span className="status coming-soon">Coming Soon</span>
                  </div>
                  <p>Sync tasks with Trello boards and cards</p>
                  <button 
                    className="connect-btn"
                    disabled
                  >
                    Coming Soon
                  </button>
                </div>

              </div>
            </div>

            {/* Meeting & Video Calls Category */}
            <div className="integration-category">
              <h4 className="category-header">🎥 Meeting & Video Calls</h4>
              <div className="integrations-grid">

                {/* Meeting Preferences */}
                <div className="integration-card">
                  <div className="integration-header">
                    <h4>⚙️ Meeting Preferences</h4>
                    <span className="status connected">Configured</span>
                  </div>
                  <p>Set your default meeting platform for faster task scheduling</p>
                  <div className="form-group" style={{ marginTop: '1rem' }}>
                    <label>Default Meeting Type:</label>
                    <select
                      value={profile.preferences.defaultMeetingType || ""}
                      onChange={(e) => handleInputChange('preferences.defaultMeetingType', e.target.value)}
                      style={{ 
                        width: '100%', 
                        padding: '0.5rem', 
                        border: '1px solid #ccc', 
                        borderRadius: '4px',
                        fontSize: '0.9rem'
                      }}
                    >
                      <option value="">None</option>
                      <option value="Zoom">🎥 Zoom</option>
                      <option value="Google Meet">📹 Google Meet</option>
                      <option value="Teams">💼 Microsoft Teams</option>
                      <option value="Custom Link">🔗 Custom Link</option>
                      <option value="Address">📍 Physical Address</option>
                    </select>
                    <small style={{ color: '#666', fontSize: '0.8rem', marginTop: '0.5rem', display: 'block' }}>
                      This will be pre-selected when creating new tasks with meetings
                    </small>
                  </div>
                  {profile.preferences.defaultMeetingType && (
                    <div style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: '#f8f9fa', borderRadius: '4px', fontSize: '0.85rem' }}>
                      <strong>✅ Currently set to:</strong> {profile.preferences.defaultMeetingType}
                    </div>
                  )}
                </div>

                {/* Zoom Integration */}
                <div className="integration-card">
                  <div className="integration-header">
                    <h4>🎥 Zoom</h4>
                    <span className={`status ${zoomConnected ? 'connected' : 'disconnected'}`}>
                      {zoomConnected ? 'Connected' : 'Not Connected'}
                    </span>
                  </div>
                  <p>Direct integration with Zoom for automatic meeting link generation</p>
                  
                  {zoomConnected && zoomUserInfo && (
                    <div className="connection-info">
                      <div className="user-info">
                        <p><strong>Account:</strong> {zoomUserInfo.email || 'Zoom User'}</p>
                        {zoomUserInfo.first_name && (
                          <p><strong>Name:</strong> {zoomUserInfo.first_name} {zoomUserInfo.last_name}</p>
                        )}
                      </div>
                    </div>
                  )}
                  
                  <div className="integration-actions">
                    {zoomConnected ? (
                      <button 
                        className="disconnect-btn"
                        onClick={handleZoomDisconnect}
                      >
                        Disconnect
                      </button>
                    ) : (
                      <button 
                        className="connect-btn"
                        onClick={handleZoomConnect}
                      >
                        Connect to Zoom
                      </button>
                    )}
                  </div>
                </div>

                {/* Google Meet Integration */}
                <div className="integration-card">
                  <div className="integration-header">
                    <h4>📹 Google Meet</h4>
                    <span className={`status ${googleToken ? 'connected' : 'disconnected'}`}>
                      {googleToken ? 'Connected' : 'Requires Google Calendar'}
                    </span>
                  </div>
                  <p>Auto-generate Google Meet links when creating tasks (requires Google Calendar connection)</p>
                  
                  {googleToken && profile.integrations.google && (
                    <div className="connection-info">
                      <div className="user-info">
                        <p><strong>Account:</strong> {profile.integrations.google.email}</p>
                        <p><strong>Name:</strong> {profile.integrations.google.name}</p>
                        <p style={{ color: '#28a745', fontSize: '0.85rem', marginTop: '0.5rem' }}>
                          ✅ Google Meet is ready! You can now create meetings with automatic Google Meet links.
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {!googleToken && (
                    <div className="connection-info">
                      <p style={{ color: '#6c757d', fontSize: '0.85rem', fontStyle: 'italic' }}>
                        Connect to Google Calendar first to enable Google Meet integration
                      </p>
                    </div>
                  )}
                  
                  <div className="integration-actions">
                    {googleToken ? (
                      <button 
                        className="connect-btn connected"
                        disabled
                        style={{ opacity: 0.8, cursor: 'not-allowed' }}
                      >
                        ✅ Ready to Use
                      </button>
                    ) : (
                      <button 
                        className="connect-btn"
                        disabled
                        style={{ opacity: 0.6 }}
                      >
                        Connect Google Calendar First
                      </button>
                    )}
                  </div>
                </div>

                {/* Microsoft Teams Integration */}
                <div className="integration-card">
                  <div className="integration-header">
                    <h4>💼 Microsoft Teams</h4>
                    <span className={`status ${teamsConnected ? 'connected' : 'disconnected'}`}>
                      {teamsConnected ? 'Connected' : 'Requires Outlook'}
                    </span>
                  </div>
                  <p>Direct integration with Microsoft Teams for automatic meeting creation (uses Outlook connection)</p>
                  
                  {teamsConnected && teamsConnectionInfo.userInfo && (
                    <div className="connection-info">
                      <div className="user-info">
                        <p><strong>Account:</strong> {teamsConnectionInfo.userInfo.email}</p>
                        <p><strong>Name:</strong> {teamsConnectionInfo.userInfo.name}</p>
                        <p style={{ color: '#28a745', fontSize: '0.85rem', marginTop: '0.5rem' }}>
                          ✅ Teams is ready! You can now create meetings with automatic Teams links.
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {!teamsConnected && (
                    <div className="connection-info">
                      <p style={{ color: '#6c757d', fontSize: '0.85rem', fontStyle: 'italic' }}>
                        Connect to Microsoft Outlook first to enable Teams integration
                      </p>
                    </div>
                  )}
                  
                  <div className="integration-actions">
                    {teamsConnected ? (
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button 
                          onClick={handleTeamsTest} 
                          className="test-btn"
                          style={{ flex: 1 }}
                        >
                          Test Teams
                        </button>
                        <button 
                          className="connect-btn connected"
                          disabled
                          style={{ opacity: 0.8, cursor: 'not-allowed', flex: 1 }}
                        >
                          ✅ Ready to Use
                        </button>
                      </div>
                    ) : (
                      <button 
                        className="connect-btn"
                        disabled
                        style={{ opacity: 0.6 }}
                      >
                        Connect Outlook First
                      </button>
                    )}
                  </div>
                </div>

              </div>
            </div>

            {/* Email Management Section */}
            <div className="integration-section">
              <h3>📧 Email Management</h3>
              <div className="integration-grid">

                {/* Aidey AI Email Assistant */}
                <div className="integration-card integration-card-active">
                  <div className="integration-header">
                    <h4>🤖 Aidey AI Email Assistant</h4>
                    <span className="status active">Available</span>
                  </div>
                  <p>AI-powered email management with smart categorization, automated replies, and action extraction</p>
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
                    <button 
                      className="connect-btn" 
                      onClick={() => window.open('/email-dashboard', '_blank')}
                      style={{ flex: 1 }}
                    >
                      Open Email Dashboard
                    </button>
                    <button 
                      className="connect-btn secondary" 
                      onClick={() => window.open('/email-settings', '_blank')}
                      style={{ flex: 1, backgroundColor: '#6c757d' }}
                    >
                      Settings
                    </button>
                  </div>
                  <div style={{ marginTop: '0.75rem', padding: '0.5rem', backgroundColor: '#e8f4f8', borderRadius: '4px', fontSize: '0.85rem' }}>
                    <strong>Features:</strong> Gmail & Outlook integration, AI categorization, smart replies, VIP handling, automated actions
                  </div>
                </div>

                {/* Gmail Integration */}
                <div className="integration-card">
                  <div className="integration-header">
                    <h4>📬 Gmail</h4>
                    <span className={`status ${googleToken ? 'connected' : 'disconnected'}`}>
                      {googleToken ? 'Connected' : 'Requires Google Calendar'}
                    </span>
                  </div>
                  <p>Direct Gmail integration for advanced email management with AI features</p>
                  
                  {googleToken && profile.integrations.google && (
                    <div className="connection-info">
                      <div className="user-info">
                        <p><strong>Account:</strong> {profile.integrations.google.email}</p>
                        <p><strong>Name:</strong> {profile.integrations.google.name}</p>
                        <p style={{ color: '#28a745', fontSize: '0.85rem', marginTop: '0.5rem' }}>
                          ✅ Gmail is ready! Full email management with send, read, and search capabilities.
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {!googleToken && (
                    <div className="connection-info">
                      <p style={{ color: '#6c757d', fontSize: '0.85rem', fontStyle: 'italic' }}>
                        Connect to Google Calendar first to enable Gmail integration
                      </p>
                    </div>
                  )}
                  
                  <div className="integration-actions">
                    {googleToken ? (
                      <button 
                        className="connect-btn" 
                        onClick={() => window.location.href = '#GmailDashboard'}
                        style={{ backgroundColor: '#4285f4' }}
                      >
                        📧 Open Gmail Dashboard
                      </button>
                    ) : (
                      <button 
                        className="connect-btn"
                        disabled
                        style={{ opacity: 0.6 }}
                      >
                        Connect Google Calendar First
                      </button>
                    )}
                  </div>
                </div>

                {/* Outlook Integration */}
                <div className="integration-card integration-card-coming-soon">
                  <div className="integration-header">
                    <h4>📮 Outlook</h4>
                    <span className="status coming-soon">Coming Soon</span>
                  </div>
                  <p>Microsoft Outlook integration for enterprise email management</p>
                  <button className="connect-btn" disabled>
                    Coming Soon
                  </button>
                </div>

              </div>
            </div>

          </div>
        )}

        {activeTab === "preferences" && (
          <div className="profile-section">
            <h3>App Preferences</h3>
            <div className="preferences-grid">
              <div className="form-group">
                <label>Theme</label>
                <select
                  value={profile.preferences.theme}
                  onChange={(e) => handleInputChange('preferences.theme', e.target.value)}
                  disabled={!isEditing}
                >
                  <option value="light">Light</option>
                  <option value="dark">Dark</option>
                  <option value="auto">Auto</option>
                </select>
              </div>
              <div className="form-group">
                <label>Default Task Duration (minutes)</label>
                <input
                  type="number"
                  value={profile.preferences.defaultTaskDuration}
                  onChange={(e) => handleInputChange('preferences.defaultTaskDuration', parseInt(e.target.value))}
                  disabled={!isEditing}
                  min="15"
                  max="480"
                />
              </div>
              <div className="form-group">
                <label>Working Hours Start</label>
                <input
                  type="time"
                  value={profile.preferences.workingHours.start}
                  onChange={(e) => handleInputChange('preferences.workingHours.start', e.target.value)}
                  disabled={!isEditing}
                />
              </div>
              <div className="form-group">
                <label>Working Hours End</label>
                <input
                  type="time"
                  value={profile.preferences.workingHours.end}
                  onChange={(e) => handleInputChange('preferences.workingHours.end', e.target.value)}
                  disabled={!isEditing}
                />
              </div>
            </div>
            <div className="checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={profile.preferences.notifications}
                  onChange={(e) => handleInputChange('preferences.notifications', e.target.checked)}
                  disabled={!isEditing}
                />
                Enable push notifications
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={profile.preferences.emailReminders}
                  onChange={(e) => handleInputChange('preferences.emailReminders', e.target.checked)}
                  disabled={!isEditing}
                />
                Send email reminders
              </label>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Profile;
